import { useState } from 'react';
import { type LoaderFunctionArgs, useLoaderData } from 'react-router';
import type { Order } from '@auto-oms/shared/types/order';
import { api } from '../lib/api';
import { StatusBadge } from '../components/ui/StatusBadge';
import { ChannelBadge } from '../components/ui/ChannelBadge';
import { OrderDetailDialog } from '../components/ui/OrderDetailDialog';
import { formatDateTime, formatKRW } from '../lib/format';

interface BatchDetailLoaderData {
  batch: {
    id: number;
    batchCode: string;
    status: string;
    excelFilename: string | null;
    emailMessageId: string | null;
    emailSentAt: string | null;
  };
  items: Array<{ id: number; orderId: number; snapshotJson: unknown }>;
  orders: Order[];
}

export async function batchDetailLoader({ params }: LoaderFunctionArgs) {
  const code = params.code!;
  return api.get<BatchDetailLoaderData>(`/fulfillment/batches/${encodeURIComponent(code)}`);
}

export function BatchDetailPage() {
  const { batch, orders } = useLoaderData() as BatchDetailLoaderData;
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);
  const total = orders.reduce((s, o) => s + o.totalPrice, 0);
  return (
    <div>
      <header>
        <p className="label-overline">발주 배치</p>
        <h1 className="mt-1 break-all font-mono text-[24px] font-light tracking-[-0.01em] text-ink-950">
          {batch.batchCode}
        </h1>
        <p className="mt-1 text-[13px] text-ink-500">
          상태 <span className="text-ink-900">{batch.status}</span> ·{' '}
          <span className="tabular text-ink-900">{orders.length}</span>건 · 합계{' '}
          <span className="tabular text-ink-900">{formatKRW(total)}</span> ·{' '}
          {batch.emailSentAt ? formatDateTime(batch.emailSentAt) : '미발송'}
        </p>
      </header>

      {/* Mobile cards */}
      <div className="mt-4 space-y-2.5 md:hidden">
        {orders.length === 0 ? (
          <div className="rounded-lg border border-ink-200 bg-paper px-4 py-10 text-center text-sm text-ink-500">
            주문이 없습니다.
          </div>
        ) : (
          orders.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setDetailOrder(o)}
              className="block w-full rounded-xl border border-ink-200 bg-paper p-4 text-left shadow-sm active:bg-ink-50"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <StatusBadge status={o.status} />
                <ChannelBadge platform={o.platform} />
              </div>
              <p className="mt-2.5 break-words text-base font-semibold leading-snug text-ink-900">{o.productName}</p>
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-400">수령인</dt>
                  <dd className="mt-0.5 truncate text-sm font-medium text-ink-800">{o.recipientName}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-400">합계</dt>
                  <dd className="mt-0.5 text-sm font-medium text-ink-800">{formatKRW(o.totalPrice)}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-400">주문번호</dt>
                  <dd className="mt-0.5 break-all font-mono text-sm text-ink-700">{o.externalOrderId}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-400">송장</dt>
                  <dd className="mt-0.5 break-all font-mono text-sm text-ink-700">
                    {o.trackingCarrier && o.trackingNumber
                      ? `${o.trackingCarrier} / ${o.trackingNumber}`
                      : <span className="font-sans text-ink-300">—</span>}
                  </dd>
                </div>
              </dl>
            </button>
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="mt-4 hidden overflow-hidden rounded-lg border border-ink-200 bg-paper md:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-sm">
            <thead className="bg-ink-50 text-ink-600">
              <tr>
                <th className="px-3 py-2 text-left">상태</th>
                <th className="px-3 py-2 text-left">채널</th>
                <th className="px-3 py-2 text-left">주문번호</th>
                <th className="px-3 py-2 text-left">상품</th>
                <th className="px-3 py-2 text-left">수령인</th>
                <th className="px-3 py-2 text-right">합계</th>
                <th className="px-3 py-2 text-left">송장</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-ink-100">
                  <td className="px-3 py-2"><StatusBadge status={o.status} /></td>
                  <td className="px-3 py-2"><ChannelBadge platform={o.platform} /></td>
                  <td className="px-3 py-2 font-mono text-xs">{o.externalOrderId}</td>
                  <td className="px-3 py-2">{o.productName}</td>
                  <td className="px-3 py-2">{o.recipientName}</td>
                  <td className="px-3 py-2 text-right">{formatKRW(o.totalPrice)}</td>
                  <td className="px-3 py-2 text-xs text-ink-600">
                    {o.trackingCarrier && o.trackingNumber
                      ? `${o.trackingCarrier} / ${o.trackingNumber}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <OrderDetailDialog
        open={detailOrder !== null}
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
      />
    </div>
  );
}
